import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, SkipForward, CheckCircle2, MessageSquareText } from "lucide-react";
import { buildFollowupResponse } from "@/lib/followup";

/**
 * FollowUpSheet — Bottom-sheet interactive pour repondre aux questions
 * generees par les agents IA via le bloc <FOLLOWUP>...</FOLLOWUP>.
 *
 * Props:
 * - open: boolean
 * - onOpenChange: (open) => void
 * - questions: [{q, type, choices, placeholder}]
 * - agentName: string ("ODYS'ANALYSE", "ODYS'GUIDE", ...)
 * - onSubmit: (text) => void   // appele avec le message construit a envoyer
 */
export default function FollowUpSheet({
  open,
  onOpenChange,
  questions,
  agentName,
  onSubmit,
}) {
  const [answers, setAnswers] = useState({});
  const [customInputs, setCustomInputs] = useState({}); // pour "Autre" sur QCM

  // Reset quand on ouvre avec de nouvelles questions
  useEffect(() => {
    if (open) {
      setAnswers({});
      setCustomInputs({});
    }
  }, [open, questions]);

  const handleSelect = (qIdx, value) => {
    setAnswers((prev) => ({ ...prev, [qIdx]: value }));
  };

  const handleCustom = (qIdx, value) => {
    setCustomInputs((prev) => ({ ...prev, [qIdx]: value }));
    setAnswers((prev) => ({ ...prev, [qIdx]: value }));
  };

  const handleSkip = (qIdx) => {
    setAnswers((prev) => ({ ...prev, [qIdx]: "__SKIP__" }));
  };

  const handleSubmitAll = () => {
    if (!questions || !questions.length) return;
    const ordered = questions.map((_, i) => answers[i] ?? "__SKIP__");
    const text = buildFollowupResponse(questions, ordered);
    onSubmit(text);
    onOpenChange(false);
  };

  const answeredCount = questions.filter(
    (_, i) => answers[i] && answers[i] !== ""
  ).length;
  const allAnswered = answeredCount === questions.length;
  const anyAnswered = answeredCount > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="fu-sheet"
        data-testid="followup-sheet"
      >
        <SheetHeader className="fu-header">
          <SheetTitle className="sr-only">Questions complementaires</SheetTitle>
          <SheetDescription className="sr-only">
            Reponds aux questions pour permettre a l'agent d'affiner sa reponse.
          </SheetDescription>
          <div className="fu-eyebrow">
            <MessageSquareText className="w-3.5 h-3.5" />
            Complete ta consultation
          </div>
          <h2 className="fu-title">
            Quelques precisions pour {agentName || "ton expert"}
          </h2>
          <p className="fu-subtitle">
            Reponds aux questions ci-dessous. Tu peux passer celles qui ne
            s'appliquent pas. Tes reponses seront envoyees comme message a
            l'agent pour qu'il affine sa reponse.
          </p>
          <div className="fu-progress">
            <span>
              {answeredCount} / {questions.length} reponses
            </span>
            <div className="fu-progress-bar">
              <div
                className="fu-progress-fill"
                style={{
                  width: `${(answeredCount / questions.length) * 100}%`,
                }}
              />
            </div>
          </div>
        </SheetHeader>

        <div className="fu-body">
          {questions.map((q, qIdx) => {
            const current = answers[qIdx];
            const skipped = current === "__SKIP__";
            return (
              <div key={qIdx} className="fu-question-block">
                <div className="fu-q-header">
                  <span className="fu-q-num">{qIdx + 1}</span>
                  <h3 className="fu-q-text">{q.q}</h3>
                  {current !== undefined && !skipped && (
                    <CheckCircle2 className="w-4 h-4 text-[color:var(--teal)] shrink-0" />
                  )}
                </div>

                {q.type === "qcm" ? (
                  <div className="fu-choices">
                    {q.choices.map((c, cIdx) => {
                      const selected = current === c;
                      return (
                        <button
                          key={cIdx}
                          type="button"
                          onClick={() => handleSelect(qIdx, c)}
                          data-testid={`fu-q${qIdx}-choice-${cIdx}`}
                          className={`fu-choice ${selected ? "fu-choice-selected" : ""}`}
                        >
                          <span className="fu-choice-letter">
                            {String.fromCharCode(65 + cIdx)}
                          </span>
                          <span>{c}</span>
                        </button>
                      );
                    })}
                    {/* Autre = input libre */}
                    <div
                      className={`fu-choice fu-choice-other ${customInputs[qIdx] ? "fu-choice-selected" : ""}`}
                    >
                      <span className="fu-choice-letter">+</span>
                      <Input
                        type="text"
                        placeholder="Autre (precise)..."
                        value={customInputs[qIdx] || ""}
                        onChange={(e) => handleCustom(qIdx, e.target.value)}
                        data-testid={`fu-q${qIdx}-other-input`}
                        className="fu-other-input"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSkip(qIdx)}
                      data-testid={`fu-q${qIdx}-skip`}
                      className={`fu-skip ${skipped ? "fu-skip-active" : ""}`}
                    >
                      <SkipForward className="w-3 h-3" />
                      Passer cette question
                    </button>
                  </div>
                ) : (
                  // type === "text"
                  <div className="fu-text-input-wrap">
                    <Input
                      type="text"
                      placeholder={q.placeholder || "Ta reponse..."}
                      value={
                        skipped ? "" : current !== undefined ? current : ""
                      }
                      onChange={(e) => handleSelect(qIdx, e.target.value)}
                      data-testid={`fu-q${qIdx}-input`}
                      className="fu-text-input"
                    />
                    <button
                      type="button"
                      onClick={() => handleSkip(qIdx)}
                      data-testid={`fu-q${qIdx}-skip`}
                      className={`fu-skip ${skipped ? "fu-skip-active" : ""}`}
                    >
                      <SkipForward className="w-3 h-3" />
                      Passer
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="fu-footer">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="fu-cancel-btn"
            className="fu-cancel"
          >
            Annuler
          </button>
          <Button
            type="button"
            onClick={handleSubmitAll}
            disabled={!anyAnswered}
            data-testid="fu-submit-btn"
            className="fu-submit"
          >
            <Send className="w-4 h-4 mr-2" />
            {allAnswered
              ? "Envoyer mes reponses"
              : anyAnswered
                ? `Envoyer (${answeredCount}/${questions.length})`
                : "Reponds a au moins une question"}
          </Button>
        </div>

        <style>{`
          .fu-sheet {
            background: var(--paper) !important;
            border-top: 1px solid var(--gold) !important;
            border-radius: 18px 18px 0 0;
            max-height: 88vh;
            overflow-y: auto;
            padding: 0 !important;
          }
          .fu-header {
            padding: 28px 28px 18px;
            border-bottom: 1px solid var(--line);
            background: linear-gradient(180deg, var(--gold-bg), transparent);
            text-align: left;
          }
          .fu-eyebrow {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-family: var(--sans);
            text-transform: uppercase;
            letter-spacing: 0.22em;
            font-size: 0.66rem;
            font-weight: 700;
            color: var(--gold);
            margin-bottom: 6px;
          }
          .fu-title {
            font-family: var(--serif);
            font-weight: 600;
            font-size: clamp(1.4rem, 3vw, 1.85rem);
            color: var(--ink);
            margin: 0;
            line-height: 1.15;
          }
          .fu-subtitle {
            font-family: var(--sans);
            color: var(--ink-soft);
            font-size: 0.92rem;
            line-height: 1.55;
            margin: 10px 0 16px;
            max-width: 62ch;
          }
          .fu-progress {
            display: flex;
            align-items: center;
            gap: 12px;
            font-family: var(--sans);
            font-size: 0.78rem;
            color: var(--muted);
            font-weight: 600;
          }
          .fu-progress-bar {
            flex: 1;
            height: 4px;
            background: var(--paper-2);
            border-radius: 2px;
            overflow: hidden;
            max-width: 280px;
          }
          .fu-progress-fill {
            height: 100%;
            background: var(--gold);
            transition: width 0.3s;
          }

          .fu-body {
            padding: 20px 28px 24px;
            display: flex;
            flex-direction: column;
            gap: 24px;
          }
          .fu-question-block {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding-bottom: 18px;
            border-bottom: 1px dashed var(--line);
          }
          .fu-question-block:last-child {
            border-bottom: none;
            padding-bottom: 0;
          }
          .fu-q-header {
            display: flex;
            align-items: flex-start;
            gap: 10px;
          }
          .fu-q-num {
            font-family: var(--serif);
            font-weight: 600;
            color: var(--gold);
            font-size: 1.4rem;
            line-height: 1;
            min-width: 1.6rem;
          }
          .fu-q-text {
            font-family: var(--serif);
            font-weight: 600;
            font-size: 1.05rem;
            color: var(--ink);
            margin: 0;
            line-height: 1.3;
            flex: 1;
          }

          .fu-choices {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
            margin-left: 26px;
          }
          @media (max-width: 640px) {
            .fu-choices { grid-template-columns: 1fr; }
          }
          .fu-choice {
            display: flex;
            align-items: center;
            gap: 10px;
            background: #fff;
            border: 1px solid var(--line);
            border-radius: 6px;
            padding: 11px 14px;
            font-family: var(--sans);
            font-size: 0.92rem;
            color: var(--ink);
            cursor: pointer;
            text-align: left;
            transition: 0.15s;
          }
          .fu-choice:hover {
            border-color: var(--gold);
            background: var(--gold-bg);
          }
          .fu-choice-selected {
            border-color: var(--gold) !important;
            background: var(--gold-bg) !important;
            box-shadow: inset 0 0 0 1px var(--gold);
          }
          .fu-choice-letter {
            font-family: var(--sans);
            font-weight: 700;
            font-size: 0.78rem;
            color: var(--gold);
            background: #fff;
            border: 1px solid var(--gold-soft);
            border-radius: 4px;
            width: 22px;
            height: 22px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: none;
          }
          .fu-choice-selected .fu-choice-letter {
            background: var(--gold);
            color: #fff;
            border-color: var(--gold);
          }
          .fu-choice-other { padding: 6px 6px 6px 14px; }
          .fu-other-input {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
            color: var(--ink) !important;
            font-size: 0.92rem !important;
            padding: 4px 0 !important;
            height: 30px !important;
            flex: 1;
            -webkit-text-fill-color: var(--ink) !important;
          }
          .fu-other-input:focus {
            outline: none !important;
            box-shadow: none !important;
          }

          .fu-text-input-wrap {
            margin-left: 26px;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .fu-text-input {
            background: #fff !important;
            border: 1px solid var(--line) !important;
            border-radius: 6px !important;
            padding: 12px 14px !important;
            font-family: var(--sans);
            font-size: 0.95rem !important;
            color: var(--ink) !important;
            height: 44px !important;
            -webkit-text-fill-color: var(--ink) !important;
          }
          .fu-text-input:focus {
            outline: none !important;
            border-color: var(--gold) !important;
            box-shadow: 0 0 0 3px rgba(181, 137, 74, 0.12) !important;
          }

          .fu-skip {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: transparent;
            border: none;
            font-family: var(--sans);
            font-size: 0.78rem;
            color: var(--muted);
            cursor: pointer;
            padding: 6px 0 0;
            transition: color 0.15s;
            align-self: flex-start;
          }
          .fu-skip:hover { color: var(--ink); }
          .fu-skip-active {
            color: var(--gold);
            font-weight: 600;
          }

          .fu-footer {
            position: sticky;
            bottom: 0;
            background: var(--paper);
            border-top: 1px solid var(--line);
            padding: 16px 28px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
          }
          .fu-cancel {
            background: transparent;
            border: none;
            font-family: var(--sans);
            font-weight: 600;
            font-size: 0.88rem;
            color: var(--muted);
            cursor: pointer;
            padding: 8px 12px;
          }
          .fu-cancel:hover { color: var(--ink); }
          .fu-submit {
            background: var(--ink) !important;
            color: #fff !important;
            font-family: var(--sans);
            font-weight: 600;
            height: 46px;
            padding: 0 1.4rem;
            border-radius: 4px;
            border: 1px solid var(--ink) !important;
            position: relative;
            overflow: hidden;
          }
          .fu-submit:hover { background: #0d1729 !important; }
          .fu-submit:disabled { opacity: 0.5; cursor: not-allowed; }
          .fu-submit::after {
            content: "";
            position: absolute;
            left: 0; right: 0; bottom: 0;
            height: 2px;
            background: var(--gold);
          }

          /* Aucun texte ne doit deborder de sa pastille de choix */
          .fu-choice { min-width: 0; overflow-wrap: anywhere; hyphens: auto; }
          .fu-choice > span:last-child { min-width: 0; flex: 1; }
          .fu-q-text { overflow-wrap: anywhere; }
          .fu-sheet { overflow-x: hidden; max-width: 100vw; }

          @media (max-width: 640px) {
            .fu-header { padding: 20px 16px 12px; }
            .fu-body { padding: 14px 16px 18px; gap: 18px; }
            .fu-footer {
              padding: 10px 16px 14px;
              flex-direction: column-reverse;
              align-items: stretch;
              gap: 8px;
            }
            .fu-submit { width: 100%; height: 48px; padding: 0 0.8rem; white-space: normal; line-height: 1.2; }
            .fu-cancel { width: 100%; padding: 10px; }
            /* le clavier mange la moitie de l'ecran : on laisse la place */
            .fu-sheet { max-height: 92vh; max-height: 92dvh; }
            .fu-title { font-size: 1.25rem; }
            .fu-subtitle { font-size: 0.86rem; margin: 8px 0 12px; }
            .fu-choices, .fu-text-input-wrap { margin-left: 0; }
            .fu-choice { padding: 12px; font-size: 0.9rem; }
            .fu-q-num { font-size: 1.15rem; min-width: 1.2rem; }
            .fu-q-text { font-size: 0.98rem; }
            .fu-progress-bar { max-width: none; }
          }
        `}</style>
      </SheetContent>
    </Sheet>
  );
}
